import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Info, LucideIcon } from 'lucide-react'

export const InfoTooltip = ({ content }: { content: React.ReactNode }) => {
  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className='text-slate-400 hover:text-slate-600 transition-colors flex-shrink-0 inline-flex items-center justify-center cursor-help'
            role='button'
            tabIndex={0}
          >
            <Info size={18} aria-hidden='true' />
          </div>
        </TooltipTrigger>
        <TooltipContent
          side='top'
          align='center'
          sideOffset={8}
          className='px-3 py-2 max-w-xs text-sm text-white rounded-md shadow-xl bg-slate-800 z-[100]'
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface LimitInputProps {
  label: string
  value: number | null
  onChange: (value: number | null) => void
  placeholder: string
  tooltipContent: React.ReactNode
  disabled: boolean
  message: string
  icon: LucideIcon
  additionalContent?: React.ReactNode
}

export const LimitInput = ({
  label,
  value,
  onChange,
  placeholder,
  tooltipContent,
  disabled,
  message,
  icon: Icon,
  additionalContent,
}: LimitInputProps) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    const numValue = inputValue === '' ? null : parseInt(inputValue, 10)
    if (numValue === null || (!isNaN(numValue) && numValue >= 0)) {
      onChange(numValue)
    }
  }

  return (
    <div>
      <div className='flex items-center gap-2'>
        <Label className='text-sm font-medium text-slate-700 min-w-[140px]'>
          {label}
        </Label>
        <Input
          type='number'
          min='0'
          value={value ?? ''}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className='w-[200px] bg-white'
        />
        <InfoTooltip content={tooltipContent} />
      </div>
      <div className='mt-2 ml-[160px] space-y-1'>
        <div className='flex items-center gap-1.5 text-xs text-slate-500'>
          <Icon className='h-3 w-3' />
          <span>{message}</span>
        </div>
        {additionalContent && (
          <div className='text-xs text-slate-400'>{additionalContent}</div>
        )}
      </div>
    </div>
  )
}
